import type { Settings } from '@brighterly/lib-core-types/customer/interfaces'

import { useSettingsStore } from '@stores/settings'

import { useCsRoutes } from '@composables/routes/cs-route.hook'

export interface SettingsPayload {
  key: string
  value: string | number | boolean | null
}

type Payload = SettingsPayload

interface Options {
  onStart?: () => void
  onSuccess?: () => void
  onError?: (err: any) => void
}

interface SettingsApiReturn {
  get: () => Promise<Settings | null>
  post: (payload: Payload, options?: Options) => Promise<Settings | null>
  postMany: (payloads: Payload[], options?: Options) => Promise<Settings | null>
}

export const useSettingsApi = (): SettingsApiReturn => {
  const { $axios } = useNuxtApp()
  const url = useCsRoutes('me.settings')!

  const store = useSettingsStore()

  const get = async (): Promise<Settings | null> => {
    try {
      const { data } = await $axios.get(url)
      const settings = data.data.settings || null

      store.setSettings(settings)
      return settings
    } catch (err) {
      console.error(err)
      return null
    }
  }

  const postMany = async (payloads: Payload[], options?: Options): Promise<Settings | null> => {
    if (!payloads.length) {
      console.warn('empty payloads for me/settings.ts postMany')
      return null
    }

    if (options?.onStart) { options.onStart() }

    try {
      const { data } = await $axios.post(url, { settings: payloads.map(({ key, value }) => ({ key, value })) })
      if (options?.onSuccess) { options.onSuccess() }
      return data.data
    } catch (err) {
      if (options?.onError) { options.onError(err) }
      console.error(err)
      return null
    }
  }

  const post = (payload: Payload, options?: Options): Promise<Settings | null> => postMany([payload], options)

  return {
    get,
    post,
    postMany,
  }
}
